// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import { VNode } from 'snabbdom';
import { Component, jsx } from 'panel';
import { defineAndMakeJsxFactory } from 'common/util/jsx';
import { panel2react } from 'common/util/react';
import { IronContextRegistry } from 'common/report/context-registry';
import { GdprStorageManager } from 'common/contexts/storage-manager/gdpr';
import { DragAndDropManager } from 'common/report/contexts/query-contexts/drag-and-drop';
import { ReportLayoutManager } from 'common/report/contexts/report-layout';
import { WindowUrlManager } from 'common/contexts/url-manager/window';
import { CurrentProjectResource, ProjectsResource } from 'common/report/model-resources/projects';
import { CurrentUserResource } from 'common/report/model-resources/users';
import { getProjectId, getWorkspaceId, isPublicMode } from 'common/report/util';
import { HistoryManager } from 'common/contexts/history-manager';
import { createBookmarkStore } from 'common/report/stores/bookmarks';
import { createDashboardStore, shareDashboardBookmarks } from 'common/report/stores/dashboards';
import { DashboardSelectorController } from 'common/report/controllers/dashboard-selector-controller';
import { publicDashboardsClient } from 'common/report/queries/app-api/public-dashboards-client';
import { dashboardsClient } from 'common/report/queries/app-api/dashboards-client';
import { QueryContextManager } from 'common/query-context';
import { ProjectTopCreatorsResource } from 'common/report/model-resources/project-top-creators';
import { LocalStorageKey } from 'common/report/constants';
import { userCookieUtils } from 'common/report/util/user-identity-from-cookie';
import { UserLocalStorageHelper } from 'common/report/user-local-storage';
import { DARK_MODE_KEY } from 'common/widgets/chrome/chrome-header/constants';
import { PersistedDarkModeState } from 'common/widgets/chrome/chrome-header/types';
import { GlobalSearchController } from 'common/report/model-resources/global-search-controller';
import { AlertLimitsResource } from 'common/report/model-resources/alert-limits';
import { ReportEditorActions } from 'common/report/contexts/report-editor-actions';
import { WarehouseSourcesResource } from 'common/report/model-resources/warehouse-sources';
import { WarehouseDataResource } from 'common/report/model-resources/warehouse-data';
import { MetricsResource } from 'common/report/model-resources/metrics';
import { BehaviorsResource } from 'common/report/model-resources/behaviors';
import { ThemesResource } from 'common/report/model-resources/themes';
import { HomePinnedCardResource } from 'common/report/model-resources/home-pinned-card-controller';
import { EventDefinitionsResource } from 'common/report/model-resources/event-definitions';
import { EventsController } from 'common/query-builder/query-block/contexts/events-controller';
import { i18next } from 'common/i18n';
import { LocalizationStore } from 'common/i18n/store';
import { ReportInfo } from 'common/report/contexts/report-info';
import { TourManager } from 'common/contexts/tours';
import { prefersDarkColorScheme } from 'common/util/dom';
import { LookupTablesResource } from 'common/report/model-resources/lookup-tables';
import { TagsResource } from 'common/report/model-resources/tags';
import { EventPropertyDefinitionsResource } from 'common/report/model-resources/property-definitions';
import { ProjectUsersResource } from 'common/report/model-resources/project-users';
import { CustomPropertiesResource } from 'common/report/model-resources/custom-properties';
import { SupportInfoResource } from 'common/report/model-resources/support-info';
import { GlobalSidenavController } from 'iron/routes/global-sidenav/controller';
import { DataGroupsResource } from 'common/report/model-resources/data-groups';
import { SpaNavManager } from 'common/contexts/spa-nav-manager';
interface JsxProps {
  attrs?: EmptyObj;
}

// This is a blank, unstyled, non-shadow-DOM component designed only to wrap existing DOM structures so it can act as a Panel Context root for the DOM descendants under it.
// This context root is designed for standard user browser environments, not automated/isolated environments like WCT.
export class BrowserContextRoot extends Component<EmptyObj, JsxProps[`attrs`], unknown, unknown, IronContextRegistry> {
  get config() {
    const localStorageManager = new GdprStorageManager(window.localStorage);
    const urlManager = new WindowUrlManager();
    const currentProject = new CurrentProjectResource({
      urlManager
    });
    const projectId = parseInt(getProjectId());
    const parsedWorkspaceId = parseInt(getWorkspaceId());
    const workspaceId = Number.isInteger(parsedWorkspaceId) ? parsedWorkspaceId : null;
    const bookmarkStore = createBookmarkStore({
      projectId,
      workspaceId
    });
    const i18n = new LocalizationStore(i18next);
    const isPublicUrl = isPublicMode(urlManager);
    const dashboardStore = createDashboardStore();
    const dashboardSelectorController = new DashboardSelectorController({
      workspaceId,
      projectId,
      bookmarkID: null,
      // only used for managing dashboard membership for a specific bookmark
      dashboardsClient: isPublicUrl ? publicDashboardsClient : dashboardsClient,
      dashboardStore: isPublicUrl ? null : dashboardStore,
      i18n
    });
    shareDashboardBookmarks(dashboardStore, bookmarkStore);
    const sessionStorageManager = new GdprStorageManager(window.sessionStorage);
    return {
      template: (): VNode => <div />,
      contexts: ["sessionStorageManager" as const, "queryContextManager" as const, "localStorageManager" as const, "urlManager" as const, "historyManager" as const, "reportLayoutManager" as const, "reportInfo" as const, "currentProject" as const, "reportEditorActions" as const, "projects" as const, "metrics" as const, "behaviors" as const, "themes" as const, "tours" as const, "homePinnedCards" as const, "eventDefinitions" as const, "lookupTables" as const, "tags" as const, "spaNavManager" as const],
      defaultContexts: {
        bookmarkStore,
        dashboardSelectorController,
        sessionStorageManager,
        localStorageManager,
        urlManager,
        reportLayoutManager: new ReportLayoutManager(),
        dragAndDropManager: new DragAndDropManager(),
        historyManager: new HistoryManager({
          mixpanel: window.mixpanel
        }),
        queryContextManager: new QueryContextManager(),
        eventsController: new EventsController(),
        i18n,
        reportInfo: new ReportInfo(),
        tours: new TourManager({
          localStorageManager,
          currentProject
        }),
        globalSidenavController: new GlobalSidenavController(),
        spaNavManager: new SpaNavManager(),
        reportEditorActions: new ReportEditorActions(),
        homePinnedCards: new HomePinnedCardResource(),
        // common model resources
        currentProject,
        currentUser: new CurrentUserResource(),
        projects: new ProjectsResource(),
        projectTopCreators: new ProjectTopCreatorsResource({
          urlManager
        }),
        warehouseSources: new WarehouseSourcesResource({
          urlManager
        }),
        warehouseData: new WarehouseDataResource(),
        globalSearchController: new GlobalSearchController({
          sessionStorageManager
        }),
        alertLimits: new AlertLimitsResource({
          urlManager
        }),
        metrics: new MetricsResource(),
        behaviors: new BehaviorsResource(),
        themes: new ThemesResource({
          currentProject
        }),
        eventDefinitions: new EventDefinitionsResource(),
        lookupTables: new LookupTablesResource(),
        tags: new TagsResource(),
        eventPropertyDefinitions: new EventPropertyDefinitionsResource(),
        projectUsers: new ProjectUsersResource(),
        customProperties: new CustomPropertiesResource(),
        dataGroups: new DataGroupsResource(),
        supportInfo: new SupportInfoResource()
      }
    };
  }
  connectedCallback(): void {
    super.connectedCallback();
    if (!isPublicMode() && getProjectId()) {
      this.getContext("currentProject").get();
    }
    this.initializeDarkMode();
  }
  initializeDarkMode() {
    var _userCookieUtils$getU;
    // mp-browser-context-root is rendered outside the context of the main product UI
    // this prevents loading a dark color scheme on components where we don't intended to
    if (!getProjectId() && !window.document.documentElement.querySelector("#mixpanel-app-wrapper")) {
      return;
    }
    const userLocalStorageDarkModeHelper: UserLocalStorageHelper<PersistedDarkModeState> = new UserLocalStorageHelper({
      context: this.getContext("localStorageManager"),
      namespace: LocalStorageKey.DarkMode
    });
    const persistedState = userLocalStorageDarkModeHelper.getItem({
      key: DARK_MODE_KEY,
      userId: (_userCookieUtils$getU = userCookieUtils.getUserIdentityFromCookie()) === null || _userCookieUtils$getU === void 0 ? void 0 : _userCookieUtils$getU.id
    });
    let enabled = false;
    if (this.getContext("urlManager").getQueryStringParam("dark-mode")) {
      // make URL param override all settings
      enabled = true;
    } else if (persistedState) {
      // use user persisted selection
      enabled = persistedState.darkMode;
    } else {
      // default to OS provided color scheme
      enabled = prefersDarkColorScheme();
    }
    if (enabled) {
      this.getContext("themes").setDarkMode(true);
    }
  }
}
export const BrowserContextRootEl = defineAndMakeJsxFactory<JsxProps>("mp-browser-context-root", BrowserContextRoot);
export const BrowserContextRootReact = panel2react<JsxProps>("mp-browser-context-root", BrowserContextRoot);